import { store, i18n } from './dependency';
import { parseDate, parseTime, parseNumber } from './parse';
import _ from 'lodash';

/**
 * Simple proxy for adding in custom validations / errors that don't conform to our standardized validations below. If you can associate it
 * to a field that's great (using the vKey property). If it doesn't fit well to any one field, e.g. validation combining multiple fields,
 * just make up a unique vKey.
 *
 * Supports error scoping as well if you follow those conventions in naming the vKey property.
 *
 * @param {String} vKey
 * @param {String} errorKey
 * @param {Array} args
 */
function addError (vKey, errorKey, args = [])
{
  store.commit('cove/errors/addError', { key: vKey, message: i18n.t(errorKey, args) });
}

/**
 * Simple proxy that can be used to add in a raw error message value in situations where we have no key, e.g. response from an AJAX call.
 * If you can associate it to a field that's great (using the vKey property). If it doesn't fit well to any one field, e.g. validation
 * combining multiple fields, just make up a unique vKey.
 *
 * Supports error scoping as well if you follow those conventions in naming the vKey property.
 *
 * @param {String} vKey
 * @param {String} errorText
 */
function addErrorText (vKey, errorText)
{
  store.commit('cove/errors/addError', { key: vKey, message: errorText });
}

/**
 * Sets the enclosing scope which will be used on subsequent validations.
 *
 * @param {String} scope
 */
function startScope (scope)
{
  store.commit('cove/errors/startScope', scope);
}

/**
 * Clears/resets the validation scope.
 */
function endScope ()
{
  store.commit('cove/errors/endScope');
}

export default {
  addError,
  addErrorText,
  startScope,
  endScope,
  /**
   * Rejects the field if it is empty.
   *
   * @param {string} value - field value
   * @param {string} labelKey - field label message key
   * @param {string} vKey - field model key
   *
   * @returns {boolean} - true if validation passes
   */
  required (value, labelKey, vKey) {
    let result = true;
    if (value === undefined || value === null || (Array.isArray(value) && value.length === 0))
    {
      result = false;
      addError(vKey, 'cove.validation.required', [i18n.t(labelKey)]);
    }

    return result;
  },
  string: {
    /**
     * Rejects the field if it is under a certain length.
     *
     * @param {string} value - field value
     * @param {number} minLength - min length of the field in characters
     * @param {string} labelKey - field label message key
     * @param {string} vKey - field model key
     *
     * @returns {boolean} - true if validation passes
     */
    minLength (value, minLength, labelKey, vKey) {
      let result = true;
      if (value !== undefined && value !== null && typeof value === 'string' && value.length < minLength)
      {
        result = false;
        addError(vKey, 'cove.validation.string.minLength', [i18n.t(labelKey), minLength]);
      }

      return result;
    },
    /**
     * Rejects the field if it is over a certain length.
     *
     * @param {string} value - field value
     * @param {number} maxLength - max length of the field in characters
     * @param {string} labelKey - field label message key
     * @param {string} vKey - field model key
     *
     * @returns {boolean} - true if validation passes
     */
    maxLength (value, maxLength, labelKey, vKey) {
      let result = true;
      if (value !== undefined && value !== null && typeof value === 'string' && value.length > maxLength)
      {
        result = false;
        addError(vKey, 'cove.validation.string.maxLength', [i18n.t(labelKey), maxLength]);
      }

      return result;
    }
  },
  date: {
    /**
     * Rejects if the date is invalid.
     *
     * @param {string} value - field value
     * @param {string} labelKey - field label message key
     * @param {string} vKey - field model key
     *
     * @returns {boolean} - true if validation passes
     */
    rejectIfInvalid (value, labelKey, vKey) {
      let result = true;
      // empty handled separately
      if (value && !parseDate(value)) {
        result = false;
        addError(vKey, 'cove.validation.date.invalid', [i18n.t(labelKey)]);
      }
      return result;
    },

    /**
     * Rejects if the first date is before the second date. This will attempt to parse dates both using configured
     * date formats and as ISO8601 strings.
     *
     * @param {string} date1 - first date in string format
     * @param {string} date2 - second date in string format
     * @param {string} labelKey1 - field 1 label message key
     * @param {string} labelKey2 - field 2 label message key
     * @param {string} vKey - field model key of field which will show error
     *
     * @returns {boolean} - true if validation passes
     */
    rejectIfBefore (date1, date2, labelKey1, labelKey2, vKey) {
      let result = true;

      let dt1 = parseDate(date1);
      let dt2 = parseDate(date2);

      if (dt1 && dt2)
      {
        if (dt1.toMillis() < dt2.toMillis()) {
          result = false;
          addError(vKey, 'cove.validation.date.before', [i18n.t(labelKey1), i18n.t(labelKey2)]);
        }
      }

      return result;
    },

    /**
     * Rejects if the first date is after the second date. This will attempt to parse dates both using configured
     * date formats and as ISO8601 strings.
     *
     * @param {string} date1 - first date in string format
     * @param {string} date2 - second date in string format
     * @param {string} labelKey1 - field 1 label message key
     * @param {string} labelKey2 - field 2 label message key
     * @param {string} vKey - field model key of field which will show error
     *
     * @returns {boolean} - true if validation passes
     */
    rejectIfAfter (date1, date2, labelKey1, labelKey2, vKey) {
      let result = true;

      let dt1 = parseDate(date1);
      let dt2 = parseDate(date2);

      if (dt1 && dt2)
      {
        if (dt1.toMillis() > dt2.toMillis()) {
          result = false;
          addError(vKey, 'cove.validation.date.after', [i18n.t(labelKey1), i18n.t(labelKey2)]);
        }
      }

      return result;
    }
  },
  integer: {
    /**
     * Rejects if the value is not a valid integer. Ignores empty values.
     *
     * @param {string} value
     * @param {string} labelKey
     * @param {string} vKey
     *
     * @return {boolean} - true if validation passes
     */
    rejectIfInvalid (value, labelKey, vKey) {
      let result = true;
      if (!_.isEmpty(value) && parseNumber(value) === null)
      {
        result = false;
        addError(vKey, 'cove.validation.integer.invalid', [i18n.t(labelKey)]);
      }

      return result;
    },

    /**
     * Rejects if the integer value is less than a given number. Ignores non-integer values.
     *
     * @param {string} value
     * @param {number} minValue
     * @param {string} labelKey
     * @param {string} vKey
     *
     * @return {boolean} - true if validation passes
     */
    rejectIfLessThan (value, minValue, labelKey, vKey) {
      let result = true;
      if (!Number.isInteger(minValue))
      {
        throw new Error('Error - comparison value must be integer');
      }

      let parsed = parseNumber(value);
      if (!_.isEmpty(value) && parsed !== null && parsed < minValue)
      {
        result = false;
        addError(vKey, 'cove.validation.integer.lessThan', [i18n.t(labelKey), minValue]);
      }

      return result;
    },

    /**
     * Rejects if the integer value is less than or equal to a given number. Ignores non-integer values.
     *
     * @param {string} value
     * @param {number} minValue
     * @param {string} labelKey
     * @param {string} vKey
     *
     * @return {boolean} - true if validation passes
     */
    rejectIfLessThanEqualTo (value, minValue, labelKey, vKey) {
      let result = true;
      if (!Number.isInteger(minValue))
      {
        throw new Error('Error - comparison value must be integer');
      }

      let parsed = parseNumber(value);
      if (!_.isEmpty(value) && parsed !== null && parsed <= minValue)
      {
        result = false;
        addError(vKey, 'cove.validation.integer.lessThanEqualTo', [i18n.t(labelKey), minValue]);
      }

      return result;
    },

    /**
     * Rejects if the integer value is greater than a given number. Ignores non-integer values.
     *
     * @param {string} value
     * @param {number} maxValue
     * @param {string} labelKey
     * @param {string} vKey
     */
    rejectIfGreaterThan (value, maxValue, labelKey, vKey) {
      let result = true;
      if (!Number.isInteger(maxValue))
      {
        throw new Error('Error - comparison value must be integer');
      }

      let parsed = parseNumber(value);
      if (!_.isEmpty(value) && parsed !== null && parsed > maxValue)
      {
        result = false;
        addError(vKey, 'cove.validation.integer.greaterThan', [i18n.t(labelKey), maxValue]);
      }

      return result;
    },
    /**
     * Rejects if the integer value is greater than or equal to a given number. Ignores non-integer values.
     *
     * @param {string} value
     * @param {number} maxValue
     * @param {string} labelKey
     * @param {string} vKey
     */
    rejectIfGreaterThanEqualTo (value, maxValue, labelKey, vKey) {
      let result = true;
      if (!Number.isInteger(maxValue))
      {
        throw new Error('Error - comparison value must be integer');
      }

      let parsed = parseNumber(value);
      if (!_.isEmpty(value) && parsed !== null && parsed >= maxValue)
      {
        result = false;
        addError(vKey, 'cove.validation.integer.greaterThanEqualTo', [i18n.t(labelKey), maxValue]);
      }

      return result;
    }
  },
  time: {
    /**
     * Rejects if the time is invalid.
     *
     * @param {string} value - field value
     * @param {string} labelKey - field label message key
     * @param {string} vKey - field model key
     *
     * @returns {boolean} - true if validation passes
     */
    rejectIfInvalid (value, labelKey, vKey) {
      let result = true;
      // empty handled separately
      if (value && !parseTime(value)) {
        result = false;
        addError(vKey, 'cove.validation.time.invalid', [i18n.t(labelKey)]);
      }
      return result;
    }
  },
  decimal: {
    /**
     * Rejects if the number amount is invalid.
     *
     * @param {string} value - field value
     * @param {string} labelKey - field label message key
     * @param {string} vKey - field model key
     *
     * @returns {boolean} - true if validation passes
     */
    rejectIfInvalid (value, labelKey, vKey) {
      let result = true;
      if (!_.isEmpty(value) && parseNumber(value) === null)
      {
        result = false;
        addError(vKey, 'cove.validation.number.invalid', [i18n.t(labelKey)]);
      }

      return result;
    },
    /**
     * Rejects if the number value is less than a given number.
     *
     * @param {string} value
     * @param {number} minValue
     * @param {string} labelKey
     * @param {string} vKey
     *
     * @return {boolean} - true if validation passes
     */
    rejectIfLessThan (value, minValue, labelKey, vKey) {
      let result = true;
      if (isNaN(parseFloat(minValue)))
      {
        throw new Error('Error - comparison value must be a number');
      }

      let parsed = parseNumber(value);
      if (!_.isEmpty(value) && parsed !== null && parsed < minValue)
      {
        result = false;
        addError(vKey, 'cove.validation.number.lessThan', [i18n.t(labelKey), minValue]);
      }

      return result;
    },
    /**
     * Rejects if the number value is less than or equal to a given number.
     *
     * @param {string} value
     * @param {number} minValue
     * @param {string} labelKey
     * @param {string} vKey
     *
     * @return {boolean} - true if validation passes
     */
    rejectIfLessThanEqualTo (value, minValue, labelKey, vKey) {
      let result = true;
      if (isNaN(parseFloat(minValue)))
      {
        throw new Error('Error - comparison value must be a number');
      }

      let parsed = parseNumber(value);
      if (!_.isEmpty(value) && parsed !== null && parsed <= minValue)
      {
        result = false;
        addError(vKey, 'cove.validation.number.lessThanEqualTo', [i18n.t(labelKey), minValue]);
      }

      return result;
    },
    /**
     * Rejects if the number value is greater than a given number.
     *
     * @param {string} value
     * @param {number} maxValue
     * @param {string} labelKey
     * @param {string} vKey
     */
    rejectIfGreaterThan (value, maxValue, labelKey, vKey) {
      let result = true;
      if (isNaN(parseFloat(maxValue)))
      {
        throw new Error('Error - comparison value must be a number');
      }

      let parsed = parseNumber(value);
      if (!_.isEmpty(value) && parsed !== null && parsed > maxValue)
      {
        result = false;
        addError(vKey, 'cove.validation.number.greaterThan', [i18n.t(labelKey), maxValue]);
      }

      return result;
    },
    /**
     * Rejects if the number value is greater than or equal to a given number.
     *
     * @param {string} value
     * @param {number} maxValue
     * @param {string} labelKey
     * @param {string} vKey
     */
    rejectIfGreaterThanEqualTo (value, maxValue, labelKey, vKey) {
      let result = true;
      if (isNaN(parseFloat(maxValue)))
      {
        throw new Error('Error - comparison value must be a number');
      }

      let parsed = parseNumber(value);
      if (!_.isEmpty(value) && parsed !== null && parsed >= maxValue)
      {
        result = false;
        addError(vKey, 'cove.validation.number.greaterThanEqualTo', [i18n.t(labelKey), maxValue]);
      }

      return result;
    }
  },
  file: {
    rejectIfInvalidType (fileContentType, possibleContentTypes, labelKey, vKey) {
      let result = true;
      if (fileContentType === undefined || fileContentType === null || fileContentType === '')
      {
        result = false;
        addError(vKey, 'cove.validation.file.unknownType', [i18n.t(labelKey)]);
      }
      else if (!possibleContentTypes.includes(fileContentType))
      {
        result = false;
        addError(vKey, 'cove.validation.file.invalidType', [i18n.t(labelKey), fileContentType]);
      }

      return result;
    },
    rejectIfGreaterThan (fileSizeInBytes, maxSizeInBytes, labelKey, vKey) {
      let result = true;
      if (fileSizeInBytes > maxSizeInBytes)
      {
        result = false;
        addError(vKey,
          'cove.validation.file.greaterThan',
          [ i18n.t(labelKey), (maxSizeInBytes / (1024 * 1024)) ]
        );
      }
      return result;
    },
    rejectIfEmpty (fileSizeInBytes, labelKey, vKey) {
      let result = true;
      if (fileSizeInBytes <= 0)
      {
        result = false;
        addError(vKey, 'cove.validation.file.empty', [ i18n.t(labelKey) ]
        );
      }
      return result;
    }
  }
};
